/**
 * Where pasted images live and how we recognise them. Images are stored in a
 * normal (non-dot) `attachments/` folder so that an image referenced from a
 * `.md` resolves in any other markdown editor too: los documentos del vault
 * siguen siendo portables aunque los canvas sean formato propio. Blanq simply
 * hides this folder from its own sidebar (presentation-only), so the user never
 * sees the raw files while still being able to view the images on the canvas.
 */

/** Root-level folder holding pasted images. Hidden from the sidebar, kept on disk. */
export const ATTACHMENTS_DIR = "attachments";

/**
 * True for folders the UI keeps out of sight. They still exist on disk and the
 * vault tree still contains them, so every consumer that walks the tree as if
 * it were "what the user can see" has to ask this — not just the sidebar.
 * Otherwise a hidden attachment (a pasted PDF, say) can be auto-opened as the
 * first document while being unreachable in the file list.
 */
export function isHiddenFolderPath(path: string): boolean {
  return path === ATTACHMENTS_DIR;
}

const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "avif"]);

/** True when a vault path points at a raster image we render as a sprite. */
export function isImagePath(path: string | undefined): boolean {
  if (!path) return false;
  const dot = path.lastIndexOf(".");
  if (dot < 0) return false;
  return IMAGE_EXT.has(path.slice(dot + 1).toLowerCase());
}

/** True when a vault path points at a PDF (opened in its own view, cover on canvas). */
export function isPdfPath(path: string | undefined): boolean {
  return !!path && path.toLowerCase().endsWith(".pdf");
}

/** True para un `src` que el navegador carga solo: no hay que buscarlo en el vault. */
export function isExternalSrc(src: string): boolean {
  return /^(https?:|mailto:|data:|blob:)/i.test(src);
}

/** Join `dir` (ending in "/") with a relative `src`, resolving `.`/`..`. */
function joinPath(dir: string, src: string): string {
  const parts = [...dir.split("/"), ...src.split("/")].filter(Boolean);
  const out: string[] = [];
  for (const p of parts) {
    if (p === ".") continue;
    if (p === "..") out.pop();
    else out.push(p);
  }
  return out.join("/");
}

/** Un destino escapado vuelve a ser una ruta del vault; si está mal formado, tal cual. */
export function decodePath(src: string): string {
  try {
    return src.split("/").map(decodeURIComponent).join("/");
  } catch {
    return src;
  }
}

/**
 * Rutas del vault donde puede estar el destino de una referencia de markdown
 * —el `src` de una imagen o el destino de un enlace—, en orden de preferencia:
 * relativa a la carpeta del documento (como Obsidian) y después relativa a la
 * raíz del vault.
 *
 * Vive acá y no en quien lo usa porque hay varios lectores del mismo markdown
 * —la vista del documento, el panel de lectura, el rasterizado de las tarjetas
 * del canvas— y si resolvieran distinto, una imagen (o un enlace) andaría en un
 * lado y en el otro no.
 */
export function pathCandidates(docPath: string, ref: string): string[] {
  const dir = docPath.slice(0, docPath.lastIndexOf("/") + 1);
  // El destino viene escapado como URL (`Pasted%20image%20…png`), pero en el
  // vault el archivo se llama con espacios: hay que desescaparlo para abrirlo.
  const path = decodePath(ref);
  return [joinPath(dir, path), path.replace(/^(?:\.\.?\/)+/, "")];
}

/** Alias histórico de `pathCandidates` para el `src` de una imagen. */
export function imageCandidates(docPath: string, src: string): string[] {
  return pathCandidates(docPath, src);
}

/** Map a clipboard image MIME type to a file extension we know how to store. */
function extForMime(mime: string): string {
  const sub = mime.split("/")[1]?.toLowerCase() ?? "png";
  if (sub === "jpeg") return "jpg";
  return IMAGE_EXT.has(sub) ? sub : "png";
}

/**
 * A stable, sortable, collision-resistant name for a pasted image, e.g.
 * `Pasted image 20260615-142233.png`. The vault's `createBinary` still
 * de-duplicates, so two pastes in the same second won't clobber.
 */
export function suggestImageName(mime: string): string {
  const d = new Date();
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  const stamp =
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  return `Pasted image ${stamp}.${extForMime(mime)}`;
}
